import React, { useEffect, useRef } from 'react';

import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';

// Custom Hook for scroll animations to keep logic out of the component.
const useScrollAnimation = () => {
    const animatedElementsRef = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px',
            }
        );

        const elements = animatedElementsRef.current;
        elements.forEach((el) => {
            if (el) observer.observe(el);
        });

        return () => {
            elements.forEach((el) => {
                if (el) observer.unobserve(el);
            });
        };
    }, []);

    return (el) => {
        if (el && !animatedElementsRef.current.includes(el)) {
            animatedElementsRef.current.push(el);
        }
    };
};

// --- Page-Specific Components ---

const ProblemSection = () => {
    const problems = [
        {
            img: "/static/about/Missed-opportunities.svg",
            alt: "Missed opportunities illustration",
            title: "Missed opportunities",
            description: "Relevant roles get missed because traditional search tools only show what fits into fixed categories."
        },
        {
            img: "/static/about/Time-consuming-process.svg",
            alt: "Time consuming process illustration",
            title: "Time-consuming process",
            description: "Endless searching across multiple platforms with nothing in one place to keep track."
        },
        {
            img: "/static/about/Nothing-is-organized.svg",
            alt: "Nothing is organized illustration",
            title: "Nothing is organized or properly traceable",
            description: "Applications and follow-ups are scattered across emails, spreadsheets, job portals, and official career pages."
        }
    ];

    return (
        <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-black dark:text-white mb-8 md:mb-12">
                The old job-finding process is constantly <span className="text-[#E03E3E] dark:text-[#FF6B6B]">failing us</span>
            </h2>
            {problems.map((problem, index) => (
                <div key={index} className="flex flex-col md:flex-row gap-8 md:gap-10 items-center mb-12">
                    <div className="w-full md:w-4/12">
                        <img src={problem.img} alt={problem.alt}
                            className="w-full max-w-[220px] md:max-w-none rounded-lg dark:invert dark:border-0 dark:bg-transparent md:mx-0 mx-0" />
                    </div>
                    <div className="w-full md:w-8/12 mt-4 md:mt-0">
                        <h3 className="text-xl md:text-2xl font-medium text-black dark:text-white mb-3">{problem.title}</h3>
                        <p className="text-[#787774] dark:text-[#9B9A97]">
                            {problem.description}
                        </p>
                    </div>
                </div>
            ))}
        </section>
    );
};

const FoundersLetter = () => {
    const addToRefs = useScrollAnimation();
    return (
        <>
            <div className="mb-12">
                <h1 ref={addToRefs} className="text-3xl md:text-4xl font-bold text-[#0B6E99] dark:text-[#7DD3FC] mb-3 reveal-item">
                    A Letter From Our Founders
                </h1>
                <p ref={addToRefs} className="lg:text-lg md:text-lg text-base text-[#787774] dark:text-[#9B9A97] reveal-item">
                    Why we're rebuilding job discovery from the ground up
                </p>
            </div>
            <div className="prose dark:prose-invert max-w-none mb-20">
                <p ref={addToRefs} className="lg:text-lg md:text-lg text-base leading-relaxed reveal-item">
                    We know there are tons of job-finding tools out there. So why are we building Trueway? Simply put—we’ve been frustrated too. We’ve missed opportunities at companies we applied to. We’ve seen roles that matched our skills—but we never even knew they existed because they were hidden in categories we didn’t think to check.
                </p>
                <p ref={addToRefs} className="lg:text-lg md:text-lg text-base leading-relaxed mt-6 reveal-item">
                    Job hunting should be simple. But right now, it’s messy and time-consuming. No real automation, no single place to track everything. We’ve had to visit 10+ different company career pages just to stay updated. And while many platforms say they use AI, most of it barely helps.
                </p>
                <p ref={addToRefs} className="lg:text-lg md:text-lg text-base leading-relaxed mt-6 reveal-item">
                    At Trueway, we’re rethinking the whole experience. No more missed opportunities. We’ve redesigned how matching and recommendations work. AI is at the core—but used the way it should be: quietly, intelligently, and for your benefit.
                </p>
                <p ref={addToRefs} className="lg:text-lg md:text-lg text-base leading-relaxed mt-6 reveal-item">
                    No clutter. No unnecessary features. With Trueway, you’ll see every role you’re eligible for—from the perfect matches, to jobs in other categories that fit your skills, to opportunities you might want to pivot into.
                </p>
                <p ref={addToRefs} className="lg:text-lg md:text-lg text-base leading-relaxed mt-6 font-medium reveal-item">
                    Job searching should feel simple, fast, and empowering. That’s what we’re here to make happen.
                </p>
                <div ref={addToRefs} className="flex flex-col items-start space-y-4 mt-12 reveal-item">
                    <img className="w-32 opacity-90 dark:opacity-100 dark:invert" src="/static/about/founders.svg" alt="Founders illustration" />
                    <p className="text-[15px] text-gray-500 dark:text-[#7DD3FC] leading-relaxed">
                        Thanks for taking the time to read this. Cheers from the founders!
                    </p>
                </div>
            </div>
        </>
    );
};

const TeamSection = () => {
    const teamMembers = [
        {
            img: "/static/about/Shohan.JPG",
            alt: "Shohan",
            name: "Mostafizur Rahman (Shohan)",
            role: "Co-Founder, Product and Business",
            linkedin: "https://www.linkedin.com/in/mrshohan/"
        },
        {
            img: "/static/about/ShahriarKabir.jpg",
            alt: "Shahriar",
            name: "Shahriar Kabir",
            role: "Co-Founder, Technology",
            linkedin: "https://www.linkedin.com/in/shahriarkabir-aero/"
        }
    ];

    return (
        <div className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-black dark:text-white mb-8">The Team</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {teamMembers.map((member, index) => (
                    <div key={index} className="space-x-6">
                        <div className="flex-shrink-0">
                            <img className="h-52 w-72 rounded-[20px] object-cover border-2 border-gray-200 dark:border-[#2A2A2A]"
                                src={member.img} alt={member.alt} />
                        </div>
                        <div>
                            <h3 className="text-lg font-normal text-black dark:text-white mt-4">{member.name}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{member.role}</p>
                            <div className="mt-4 flex space-x-4">
                                <a href={member.linkedin} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[#0a66c2] dark:text-[#70b5f9] bg-[#f0f0f0] hover:bg-[#e8e7e4] dark:hover:bg-[#363636] dark:bg-[#1d1d1d] transition-colors duration-200">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                    </svg>
                                    <span className="text-sm">Connect</span>
                                </a>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};


// --- Main Page Component ---
const About = () => {
    return (
        <>
            {/* This is the CSS needed for the reveal animation. */}
            <style>{`
        .reveal-item {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .reveal-item.is-visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
            <div className="bg-white dark:bg-[#141414] text-black dark:text-[#D9D9D9] transition-colors duration-300 min-h-screen flex flex-col">
                <Navbar />
                <main className="flex-grow py-20 px-8">
                    <div className="max-w-4xl mx-auto">
                        <ProblemSection />
                        <FoundersLetter />
                        <TeamSection />
                    </div>
                </main>
                <Footer />
            </div>
        </>
    );
};

export default About;
